import React, { Suspense, useEffect } from 'react';
import { lazyWithRetry } from '@maat/components-errors';
import { Redirect, useLocation } from 'react-router-dom';
import { useAuth } from '@maat/store-auth';
import { ROUTES } from '@maat/router';
import { PageSkeleton } from '@maat/ds-core';
import { AppContext, setActiveApp } from '@maat/store-error';

/**
 * Collapses repeated slashes and drops a trailing one — a reachable URL, not
 * defensive programming: Didit's callback is composed from `PUBLIC_WEB_BASE_URL`,
 * which `z.string().url()` accepts with a trailing slash, and the resulting
 * `//verification/callback` matches nothing here or in react-router.
 */
const normalizePathname = (pathname: string): string =>
    pathname.replace(/\/{2,}/g, '/').replace(/(.)\/+$/, '$1');

// These pages belong to the site, not a profile, and live only in the guest remote.
// Become a host is left out on purpose: a host has nothing to sign up for.
const SITE_PAGES: ReadonlySet<string> = new Set([ROUTES.ABOUT, ...Object.values(ROUTES.LEGAL)]);

const GuestRouter = lazyWithRetry(() => import('guest/GuestRouter'));
const HostRouter = lazyWithRetry(() => import('host_app/HostRouter'));
const AdminRouter = lazyWithRetry(() => import('admin/AdminRouter'));

export const AppRouter = () => {
    const { activeProfile } = useAuth();
    const { pathname, search, hash } = useLocation();

    // A separate audience with its own admin_token session: mounted purely by URL,
    // independent of `activeProfile`, and never pulls the guest/host bundles.
    const isAdminMode =
        pathname === ROUTES.ADMIN.ROOT || pathname.startsWith(`${ROUTES.ADMIN.ROOT}/`);

    // Mounted by URL for the same reason admin is: it belongs to a person, not a
    // profile, and the page lives only in the guest remote — picking by
    // `activeProfile` would 404 a signed-in host.
    const isIdNoticeMode = pathname.startsWith('/id-notice/');

    // Same rule: Didit returns the browser to one fixed URL, and an identity
    // belongs to a person (Personal-host KYC is the same record), so a host coming
    // back must not be handed the host remote. Compared on the NORMALISED
    // pathname, so a doubled or trailing slash still selects guest.
    const canonicalPathname = normalizePathname(pathname);
    const isVerificationCallbackMode = canonicalPathname === ROUTES.VERIFICATION.CALLBACK;

    const isSitePageMode = SITE_PAGES.has(canonicalPathname);

    const isHostMode =
        activeProfile === 'host' &&
        !isIdNoticeMode &&
        !isVerificationCallbackMode &&
        !isSitePageMode;

    // Keeps the single shell-owned Sentry client tagging every event with the
    // remote the user is actually in.
    useEffect(() => {
        setActiveApp(
            isAdminMode ? AppContext.Admin : isHostMode ? AppContext.Host : AppContext.Guest,
        );
    }, [isAdminMode, isHostMode]);

    // Selecting the guest remote is only half the fix: react-router inside it
    // matches on the RAW pathname, so `//verification/callback` reaches a remote
    // that matches nothing. Query and fragment are preserved — the provider may
    // append its own.
    if (isVerificationCallbackMode && pathname !== canonicalPathname) {
        return <Redirect to={{ pathname: canonicalPathname, search, hash }} />;
    }

    return (
        <Suspense fallback={<PageSkeleton />}>
            {isAdminMode ? <AdminRouter /> : isHostMode ? <HostRouter /> : <GuestRouter />}
        </Suspense>
    );
};
